/**
 * Enhanced Feature Flag System
 * Provides unified feature management across SaaS and Enterprise deployments
 */

import { deploymentConfig, DeploymentType } from './deployment';

export class FeatureFlags {
  private static instance: FeatureFlags;
  private config = deploymentConfig;

  private constructor() { }

  public static getInstance(): FeatureFlags {
    if (!FeatureFlags.instance) {
      FeatureFlags.instance = new FeatureFlags();
    }
    return FeatureFlags.instance;
  }

  /**
   * Get current deployment type
   */
  public getDeploymentType(): DeploymentType {
    return this.config.type;
  }

  /**
   * Check if running in SaaS mode
   */
  public isSaaS(): boolean {
    return this.config.type === 'saas';
  }

  /**
   * Check if running in Enterprise mode
   */
  public isEnterprise(): boolean {
    return this.config.type === 'enterprise';
  }

  // Core features (always available)
  public isNetworkDiagramsEnabled(): boolean {
    return this.config.features.core.networkDiagrams;
  }

  public isServerlessDiagramsEnabled(): boolean {
    return this.config.features.core.serverlessDiagrams;
  }

  public isAccountManagementEnabled(): boolean {
    return this.config.features.core.accountManagement;
  }

  public isErrorHandlingEnabled(): boolean {
    return this.config.features.core.errorHandling;
  }

  // Shared features
  public isMultiAccountEnabled(): boolean {
    return this.config.features.shared.multiAccount;
  }

  public isRegionPreferencesEnabled(): boolean {
    return this.config.features.shared.regionPreferences;
  }

  public isSettingsEnabled(): boolean {
    return this.config.features.shared.settings;
  }

  public isTransitGatewayEnabled(): boolean {
    return this.config.features.shared.transitGateway;
  }

  // SaaS features
  public isSubscriptionsEnabled(): boolean {
    return this.config.features.saas.subscriptions;
  }

  public isStripeEnabled(): boolean {
    return this.config.features.saas.stripe;
  }

  public isAnalyticsEnabled(): boolean {
    return this.config.features.saas.analytics;
  }

  public isSelfSignupEnabled(): boolean {
    return this.config.features.saas.selfSignup;
  }

  // Enterprise features
  public isCustomBrandingEnabled(): boolean {
    return this.config.features.enterprise.customBranding;
  }

  public isSSOIntegrationEnabled(): boolean {
    return this.config.features.enterprise.ssoIntegration;
  }

  public isPrivateAccessEnabled(): boolean {
    return this.config.features.enterprise.privateAccess;
  }

  public isCustomDomainEnabled(): boolean {
    return this.config.features.enterprise.customDomain;
  }

  public isDedicatedSupportEnabled(): boolean {
    return this.config.features.enterprise.dedicatedSupport;
  }

  /**
   * Get all enabled features for current deployment
   */
  public getEnabledFeatures(): string[] {
    const features: string[] = [];

    // Check all feature categories
    const { core, shared, saas, enterprise } = this.config.features;

    // Core features
    Object.entries(core).forEach(([key, enabled]) => {
      if (enabled) features.push(`core.${key}`);
    });

    // Shared features  
    Object.entries(shared).forEach(([key, enabled]) => {
      if (enabled) features.push(`shared.${key}`);
    });

    // SaaS features
    Object.entries(saas).forEach(([key, enabled]) => {
      if (enabled) features.push(`saas.${key}`);
    });

    // Enterprise features
    Object.entries(enterprise).forEach(([key, enabled]) => {
      if (enabled) features.push(`enterprise.${key}`);
    });

    return features;
  }
}

// Export singleton instance
export const featureFlags = FeatureFlags.getInstance();